import { useDispatch } from 'react-redux';
import {
  setTooltipArrowOffset,
  tooltipSelectors,
} from '../../reducers/tooltipSlice';
import { useAppSelector } from '../../reducers/rootReducer';
import {
  ChartsWithoutLeftContainerOffset,
  ChartsWithRightContainerOffset,
  ChartTypes,
} from '../components/PositionedTooltip/types';
import {
  CHARTS_OFFSETS,
  KPI_LINE_CHART_ARROW_OFFSET,
  VDE_LINE_CHART_ARROW_OFFSET,
} from '../components/PositionedTooltip/constants';
import {
  TOOLTIP_ARROW_LEFT_MARGIN,
  TOOLTIP_ARROW_RIGHT_MARGIN,
} from '../../components/AstronautDataExplorer/constants';

type UseTooltipArrowCoordinatesProps = {
  containerRef: React.RefObject<HTMLDivElement>;
  tooltipHeight: number;
  initialTooltipWidth?: number;
  offsetY?: number;
  chartType?: ChartTypes;
};

export default function useTooltipArrowCoordinates({
  containerRef,
  tooltipHeight,
  initialTooltipWidth,
  offsetY = 20,
  chartType,
}: UseTooltipArrowCoordinatesProps) {
  const dispatch = useDispatch();
  const activeTooltipWidth = useAppSelector(
    tooltipSelectors.selectTooltipActiveTooltipWidth,
  );
  const tooltipWidth = initialTooltipWidth || activeTooltipWidth;

  const mouseMoveHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) {
      return;
    }

    const isWithoutContainerOffset =
      chartType &&
      Object.values(ChartsWithoutLeftContainerOffset).includes(
        chartType as unknown as ChartsWithoutLeftContainerOffset,
      );

    const isWithContainerOffset =
      chartType &&
      !Object.values(ChartsWithRightContainerOffset).includes(
        chartType as unknown as ChartsWithRightContainerOffset,
      );
    const rect = containerRef.current?.getBoundingClientRect();

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    let left = mouseX;
    let top = mouseY - tooltipHeight - offsetY;

    let isOverflowingRight = false;
    let isOverflowingLeft = false;

    if (left + tooltipWidth / 2 > rect.width) {
      left = isWithoutContainerOffset
        ? rect.width - tooltipWidth
        : rect.width - tooltipWidth / 2 - rect.left;
      isOverflowingRight = true;
    }

    if (
      left - tooltipWidth / 2 < VDE_LINE_CHART_ARROW_OFFSET &&
      chartType === ChartTypes.VDELineChart
    ) {
      left = VDE_LINE_CHART_ARROW_OFFSET;
      isOverflowingLeft = true;
    }

    if (
      left + tooltipWidth / 2 >= rect.width - VDE_LINE_CHART_ARROW_OFFSET &&
      chartType === ChartTypes.VDELineChart
    ) {
      left = rect.width - tooltipWidth;
      isOverflowingRight = true;
    }

    if (
      left - tooltipWidth / 2 < 0 &&
      chartType !== ChartTypes.VDELineChart &&
      !isOverflowingRight
    ) {
      left = 0;
      isOverflowingLeft = true;
    }

    if (
      chartType === ChartTypes.KPILineChart &&
      left - tooltipWidth / 2 < KPI_LINE_CHART_ARROW_OFFSET
    ) {
      left = KPI_LINE_CHART_ARROW_OFFSET;
      isOverflowingLeft = true;
    }

    if (top < 0) {
      top = mouseY + offsetY;
    }
    let arrowOffset = '50%';
    if (isOverflowingRight || isOverflowingLeft) {
      // TODO: Refactor needed
      let relativeMouseX =
        isOverflowingRight &&
        isWithContainerOffset &&
        chartType !== ChartTypes.CardKpiLine
          ? mouseX - left + rect.left
          : chartType === ChartTypes.VDELineChart && isOverflowingRight
          ? mouseX - left + VDE_LINE_CHART_ARROW_OFFSET / 2
          : chartType === ChartTypes.KPILineChart && isOverflowingRight
          ? mouseX - left + CHARTS_OFFSETS.KPILineChart.offsetX
          : mouseX - left;

      if (isOverflowingLeft && chartType === ChartTypes.KPITimeLine) {
        relativeMouseX -= TOOLTIP_ARROW_LEFT_MARGIN;
      }

      let clampedMouseX = Math.min(
        Math.max(relativeMouseX, TOOLTIP_ARROW_LEFT_MARGIN),
        tooltipWidth - TOOLTIP_ARROW_RIGHT_MARGIN,
      );

      if (isOverflowingRight && chartType === ChartTypes.VectorSystem) {
        clampedMouseX += 80;
      }

      arrowOffset = `${clampedMouseX}px`;
    }
    dispatch(setTooltipArrowOffset(arrowOffset));
  };

  const mouseLeaveHandler = () => {
    dispatch(setTooltipArrowOffset('50%'));
  };

  return { mouseMoveHandler, mouseLeaveHandler };
}
